import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Hotel, Paperclip, MapPin, SearchX } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { HotelSearchBar } from "./HotelSearchBar";
import { HotelCard } from "./HotelCard";
import { HotelDetail } from "./HotelDetail";
import { HotelFilters, DEFAULT_FILTERS, applyFilters } from "./HotelFilters";
import { SaveStayDialog } from "./SaveStayDialog";

const SORTS = [["recommended", "Recommended"], ["price_asc", "Price: low to high"], ["price_desc", "Price: high to low"], ["rating", "Top rated"], ["stars", "Star rating"]];
const plus = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x.toISOString().slice(0, 10); };
const today = new Date().toISOString().slice(0, 10);

function Skeletons() {
  return (
    <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="hotel-results-loading">
      {Array.from({ length: 6 }).map((_, i) => (
        <div key={i} className="overflow-hidden rounded-3xl bg-card ring-1 ring-border/50">
          <div className="aspect-[4/3] animate-pulse bg-secondary" />
          <div className="space-y-2 p-4"><div className="h-4 w-2/3 animate-pulse rounded bg-secondary" /><div className="h-3 w-1/2 animate-pulse rounded bg-secondary" /><div className="h-3 w-full animate-pulse rounded bg-secondary" /><div className="mt-3 h-6 w-1/3 animate-pulse rounded bg-secondary" /></div>
        </div>
      ))}
    </div>
  );
}

export function HotelBookings({ tripContext }) {
  const navigate = useNavigate();
  const validDepart = tripContext.depart && tripContext.depart >= today ? tripContext.depart : "";
  const [stay, setStay] = useState({ destination: null, checkIn: validDepart || plus(today, 7), checkOut: (validDepart && tripContext.ret > validDepart ? tripContext.ret : "") || plus(validDepart || today, validDepart ? 2 : 9), adults: 2, children: 0, rooms: 1 });
  const [sort, setSort] = useState("recommended");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [searched, setSearched] = useState(null);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [saving, setSaving] = useState(null);
  const visible = results ? applyFilters(results.results, filters) : [];

  const runSearch = async (s = stay, srt = sort) => {
    if (!s.destination) return;
    setLoading(true); setSelected(null);
    try {
      const params = { city_id: s.destination.city_id, check_in: s.checkIn, check_out: s.checkOut, adults: s.adults, children: s.children, rooms: s.rooms, sort: srt };
      const res = await api.get("/travel/hotels/search", { params });
      setResults(res.data); setSearched({ ...s }); setFilters(DEFAULT_FILTERS);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Search failed."); }
    finally { setLoading(false); }
  };

  // Prefill (and auto-search) from the trip the user is planning
  useEffect(() => {
    if (!tripContext.dest) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get("/travel/hotels/destinations", { params: { q: tripContext.dest } });
        const best = res.data.find((d) => d.hotels > 0) || res.data[0];
        if (!best || cancelled) return;
        const next = { ...stay, destination: best };
        setStay(next); runSearch(next, sort);
      } catch { /* user can search manually */ }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tripContext.dest]);

  const changeSort = (v) => { setSort(v); if (searched) runSearch(searched, v); };
  const destName = searched?.destination?.name || stay.destination?.name || tripContext.dest || "";

  return (
    <div data-testid="bookings-tab">
      {tripContext.tid && (
        <div className="mb-5 flex flex-wrap items-center gap-3 rounded-2xl bg-accent/5 px-4 py-3 ring-1 ring-accent/20" data-testid="bookings-trip-context">
          <MapPin className="h-4 w-4 text-accent" />
          <p className="min-w-0 flex-1 text-sm"><span className="font-semibold">Trip to {tripContext.dest}</span>{tripContext.depart && <span className="text-muted-foreground"> · {tripContext.depart}{tripContext.ret ? ` → ${tripContext.ret}` : ""}</span>}</p>
          <Button size="sm" variant="ghost" className="text-accent hover:bg-accent/10 hover:text-accent" onClick={() => navigate(`/travel?tab=trips&trip=${tripContext.tid}`)} data-testid="attach-confirmation-nudge"><Paperclip className="mr-1.5 h-3.5 w-3.5" /> Attach confirmation</Button>
        </div>
      )}

      <AnimatePresence mode="wait">
        {selected ? (
          <HotelDetail key="detail" hotelId={selected.hotel_id} stay={searched || stay} destination={destName} onBack={() => setSelected(null)} onSave={setSaving} />
        ) : (
          <motion.div key="search" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
            <HotelSearchBar value={stay} onChange={setStay} onSearch={() => runSearch()} loading={loading} />

            {loading ? <div className="mt-8"><Skeletons /></div> : results ? (
              <div className="mt-8">
                <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm text-muted-foreground" data-testid="hotel-results-summary"><span className="font-semibold text-foreground">{visible.length}</span>{visible.length !== results.count ? ` of ${results.count}` : ""} stays in {destName} · {results.nights} night{results.nights > 1 ? "s" : ""}</p>
                  <Select value={sort} onValueChange={changeSort}>
                    <SelectTrigger className="w-48 rounded-xl" data-testid="hotel-sort-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{SORTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {results.count > 0 && <HotelFilters results={results.results} filters={filters} onChange={setFilters} />}
                {visible.length === 0 ? (
                  <div className="flex flex-col items-center rounded-3xl bg-card px-6 py-16 text-center ring-1 ring-border/50" data-testid="hotel-results-empty">
                    <SearchX className="h-8 w-8 text-muted-foreground" />
                    <p className="mt-3 font-display text-lg font-semibold">No stays found</p>
                    <p className="mt-1 max-w-sm text-sm text-muted-foreground">{results.count > 0 ? "No stays match these filters — loosen them a little." : "Try nearby dates or a broader destination."}</p>
                    {results.count > 0 && <Button variant="outline" size="sm" className="mt-4 rounded-xl" onClick={() => setFilters(DEFAULT_FILTERS)} data-testid="hotel-filters-clear">Clear filters</Button>}
                  </div>
                ) : (
                  <div className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3" data-testid="hotel-results">
                    {visible.map((h, i) => <HotelCard key={h.hotel_id} hotel={h} index={i} nights={results.nights} destination={destName} onOpen={setSelected} onSave={setSaving} />)}
                  </div>
                )}
              </div>
            ) : (
              <div className="mt-10 flex flex-col items-center px-6 py-14 text-center animate-fade-up" data-testid="hotel-search-intro">
                <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-accent/10 text-accent"><Hotel className="h-7 w-7" /></div>
                <h3 className="mt-4 font-display text-lg font-semibold">Find a place to stay</h3>
                <p className="mt-1.5 max-w-sm text-sm text-muted-foreground">Search live hotel rates for your next shoot, meeting or client trip — then book in a couple of taps.</p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
      {saving && <SaveStayDialog hotel={saving} stay={searched || stay} destination={destName} defaultTripId={tripContext.tid} onClose={() => setSaving(null)} />}
    </div>
  );
}
